import { IClinicListResponseData } from "@/api_framework/api_modals/Clinics";
import { IOrgCityListResponseData, IOrgProviderListResponseData } from "@/api_framework/api_modals/Org";
import { IEMRCustomerProfileSectionFormFields } from "@/app/components/routes/auth_routes/CustomerProfile/EMRCreation/Sections/CustomerPetDetailsSection";
import { IEMRCreationFormFields } from "@/app/routes/auth_routes/EMRCreationPage/interface";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { IRouteReducerSliceStateInit } from "../slice_interface";
import { IHealthCertificateResponseData } from "@/api_framework/api_modals/EMRs";

export type EMRModeType = "EDIT" | "CREATE" | "PDF_PREVIEW";

export type LatestEMRDetails = {
  createdAt: number;
  createdBy: string;
  emrId: string;
  cartId: string;
  status: string;
  customerDetails: IEMRCustomerProfileSectionFormFields;
};

export interface ICustomerEMRState {
  taperingIndex: number;
  mode: EMRModeType;
  removed_children: Array<string>;
  activeEMRId: string;
  submitToggle: boolean;
  saveDraftToggle: boolean;
  saveToggle: boolean;
  overdraft: boolean;
  formFields?: IEMRCreationFormFields;
  clinics?: IClinicListResponseData[] | null;
  latestEMRDetails?: LatestEMRDetails | null;
  cities?: IOrgCityListResponseData[] | null;
  providers?: IOrgProviderListResponseData[] | null;
  emrHealthCertificateDetails?: IHealthCertificateResponseData | null;
}

export interface ICustomerEMRInitialState extends IRouteReducerSliceStateInit {
  state: ICustomerEMRState;
}

const initialState: ICustomerEMRInitialState = {
  state: {
    taperingIndex: -1,
    mode: "CREATE",
    overdraft: false,
    removed_children: [],
    activeEMRId: null,
    submitToggle: false,
    saveDraftToggle: false,
    saveToggle: false,
    formFields: {
      customer_profile_section: null,
      diagnosis_section: null,
      vitals_section: null,
      history_section: null,
      lab_test_section: { normal_tests: [], recommended_tests: [] },
      symptoms_section: null,
      treatment_section: null,
    },
    emrHealthCertificateDetails: null,
  },
};

const customerEMRSlice = createSlice({
  name: "customer_emr_slice",
  initialState: initialState,
  reducers: {
    setLatestEMRDetails: (state, action: PayloadAction<LatestEMRDetails>) => {
      state.state.latestEMRDetails = action.payload;
    },
    setEMROverDraft: (state, action: PayloadAction<boolean>) => {
      state.state.overdraft = action.payload;
    },
    setRemovedChildren: (state, action: PayloadAction<any>) => {
      state.state.removed_children = action.payload;
    },
    setEMRMode: (state, action: PayloadAction<EMRModeType>) => {
      state.state.mode = action.payload;
    },
    setEMROrgCities: (
      state,
      action: PayloadAction<{
        data: IOrgCityListResponseData[];
      }>,
    ) => {
      state.state.cities = action.payload.data;
    },
    setEMROrgProviders: (
      state,
      action: PayloadAction<{
        data: IOrgProviderListResponseData[];
      }>,
    ) => {
      state.state.providers = action.payload.data;
    },
    setEMROrgClinics: (
      state,
      action: PayloadAction<{
        data: IClinicListResponseData[];
      }>,
    ) => {
      state.state.clinics = action.payload.data;
    },
    setActiveEMRId: (state, action: PayloadAction<string>) => {
      state.state.activeEMRId = action.payload;
    },
    setTaperingIndex: (state, action: PayloadAction<number>) => {
      state.state.taperingIndex = action.payload;
    },
    setEMRSubmitToggle: (state, action: PayloadAction<boolean>) => {
      state.state.submitToggle = action.payload;
    },
    setEMRDraftSaveToggle: (state, action: PayloadAction<boolean>) => {
      state.state.saveDraftToggle = action.payload;
    },
    setEMRSaveToggle: (state, action: PayloadAction<boolean>) => {
      state.state.saveToggle = action.payload;
    },
    setEMRFormFields: (state, action: PayloadAction<{ formFields: IEMRCreationFormFields }>) => {
      state.state.formFields = action.payload.formFields;
    },
    setEmrHealthCertificateDetails: (state, action: PayloadAction<{ data: IHealthCertificateResponseData }>) => {
      state.state.emrHealthCertificateDetails = action.payload.data;
    },
    /* Section Actions */
  },
});

export const {
  setEMRSubmitToggle,
  setEMRFormFields,
  setEMRSaveToggle,
  setTaperingIndex,
  setEMROverDraft,
  setEMROrgCities,
  setEMROrgClinics,
  setActiveEMRId,
  setEMROrgProviders,
  setLatestEMRDetails,
  setEMRDraftSaveToggle,
  setEMRMode,
  setRemovedChildren,
  setEmrHealthCertificateDetails,
} = customerEMRSlice.actions;
export default customerEMRSlice.reducer;
